import React, { useMemo, useCallback } from 'react'
import Modal from '../modal'
import Input from '../input'
import Table from '../table'

import useModel from '../../common/useModel'
import { connect } from 'react-redux'
import api, { apiAction, apiActionWithRoute } from '../../api'

import S from './styled'
import modalActions from './actions'
import columns from './columns'

const sorter = (i1, i2) => i1.name.localeCompare(i2.name)

const IngredientsModal = ({
  ingredients = [],
  onClose,
  onChange,
  fetchIngredients,
  fetchFoodKinds,
  fullWidth,
  defaultLocale,
  filter,
  foodKinds = [],
  selected = [],
  multi = true,
  title = 'Add an Ingredient',
  dontFetch,
  groupItems,
  fetchIngredient,
  disabled,
  pagination,
  isLoading,
  returnOriginals,
}) => {

  const initialModel = useMemo(() => ({
    values: selected,
  }), [selected])

  const { values, model, onChange: changeModel } = useModel(initialModel)

  const {
    handleSelectItem,
    searchText,
    actions,
    searchValue,
  } = modalActions({
    fetchIngredients, fetchFoodKinds, onChange, values, changeModel, onClose, model, multi, selected,
    ingredients,
    groupItems,
    fetchIngredient,
    disabled,
    dontFetch,
    returnOriginals,
  })

  const filteredItems = useMemo(() => {
    if (filter) {
      const usedIds = filter.map(i => i.id || i.ingredientID)
      return ingredients.filter(i => !usedIds.includes(i.id || i.ingredientID))
    } else {
      return ingredients
    }
  }, [filter, ingredients])

  const filteredIngredients = useMemo(() => {
    return (searchValue  && dontFetch ? filteredItems.filter(
      ingredient => {
        const toCheck = ingredient.name.toLocaleLowerCase()
        return (
          values.includes(ingredient) ||
          searchValue.toLocaleLowerCase().split(' ').every(value => toCheck.includes(value))
        )
      },
    ) : filteredItems).sort(sorter)
  }, [filteredItems, searchValue, values, dontFetch])

  const tableColumns = useMemo(
    () => columns({ foodKinds, selectedList: values, handleSelectItem, groupItems, disabled }),
    [disabled, foodKinds, groupItems, handleSelectItem, values])

  const handleChangePage = useCallback(({ selected }) => {
    if (selected !== pagination.page) {
      fetchIngredients(selected, searchValue)
    }
  }, [pagination.page, searchValue]) //eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal isOpened actions={actions} noScroll padding={'10px 20px'} flexRow fullWidth={fullWidth} gray>
      <S.Library>
        <div>
          <S.Title>{title}</S.Title>
        </div>
        <Input {...{
          name: 'search',
          text: searchText,
          title: 'Search',
          placeholder: 'Type ingredient name to search',
        }}
        />
        <S.LibraryList>
          <Table
            columns={tableColumns}
            rows={filteredIngredients}
            noHeader noAbsolute
            getKey={(item, index) => `${item.id}_${item.section}_${index}`}
            {...(groupItems ? {
              groupItemsBy: 'section',
              groupNameField: 'sectionTitle',
            } : null)}
            pagination={dontFetch ? null : pagination}
            onPageChange={handleChangePage}
            isLoading={isLoading}
          />
        </S.LibraryList>
      </S.Library>
    </Modal>
  )
}

const mapStateToProps = state => ({
  ingredients: state.ingredients.list,
  isLoading: state.ingredients.isLoading,
  foodKinds: state.foodKinds.list,
  pagination: state.ingredients.pagination,
})

const mapDispatchToProps = dispatch => ({
  fetchFoodKinds: () => dispatch(apiAction(api.getFoodKinds)()),
  fetchIngredients: (page = 0, search = '') => dispatch(apiAction(api.getIngredientHeaders)(
    { page, searchUniqueName: search  },
  )),
  fetchIngredient: id => dispatch(apiActionWithRoute(api.getIngredient)(id)()),
})

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
})

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(IngredientsModal)
